import { RecipeMode } from 'src/app/app.enums';
import type { CalorieUom } from 'src/app/utils/calorie-density.util';

export interface UrcInputProduct {
	id: string;
	name: string;
	targetCaloricDensity: number;
}

export interface UrcFinalDesiredVolumeInput {
	desiredVolumeInMl: number;
}

export interface UrcVolumeOnHandInput {
	volumeOnHandInMl: number;
}

export interface UrcUserInput {
	products: UrcInputProduct[];
	/** Unit for every `targetCaloricDensity` here; request-level, since one order drives all rows. */
	caloricDensityUnit: CalorieUom;
	finalDesiredVolumeInput?: UrcFinalDesiredVolumeInput;
	volumeOnHandInput?: UrcVolumeOnHandInput;
}

export interface UrcProductResult {
	productId: string;
	name: string;
	amountNeeded: number;
	unitOfMeasure: string;
	caloricDensity: number;
	fortifierVolumeInMl: number;
	fortifierVolumeInOz: number;
}

export interface UrcResult {
	mode: RecipeMode;
	finalVolume: number;
	finalCaloricDensityKcalPerOz: number;
	/** Final density in kcal/mL — the calculator's fundamental unit. */
	finalCaloricDensityKcalPerMl: number;
	products: UrcProductResult[];
}

/**
 * Why a recipe could not be produced under the products' UOM (incremental) constraints.
 * Mirrors the backend `RecipeNotSatisfiableError.Reason`.
 *
 * - `NotEnoughBase`: the base on hand does not cover one whole increment of the constrained
 *   product; `requiredBaseVolumeInMl` says how much would.
 * - `MultipleConstrainedProducts`: more than one selected product carries a UOM constraint, which
 *   the calculator does not currently support.
 */
export type UrcNotSatisfiableReason = 'NotEnoughBase' | 'MultipleConstrainedProducts';

/** A single product's UOM (incremental) constraint. Mirrors the backend `ConstraintInfo`. */
export interface UrcConstraintInfo {
	productId: string;
	productName: string;
	incrementalConstraint: number;
	unitOfMeasure: string;
}

/**
 * Structured 422 body returned by the calculator when no recipe can satisfy every product's UOM
 * (incremental) constraint. Mirrors the backend `RecipeNotSatisfiableError`.
 */
export interface UrcNotSatisfiableError {
	reason: UrcNotSatisfiableReason;
	message: string;
	requestedVolumeInMl: number;
	/** Smallest base volume (mL) that would produce a recipe; null for other reasons. */
	requiredBaseVolumeInMl: number | null;
	constraints: UrcConstraintInfo[];
}

/** Thrown by {@link UniversalRecipeCalculatorService} when the calculator returns a 422. */
export class RecipeNotSatisfiableApiError extends Error {
	constructor(public readonly details: UrcNotSatisfiableError) {
		super(details.message);
		this.name = 'RecipeNotSatisfiableApiError';
		// Restore the prototype chain so `instanceof` works after TS down-compilation.
		Object.setPrototypeOf(this, RecipeNotSatisfiableApiError.prototype);
	}
}
